// utils/session.js — ต่ออายุเซสชันอัตโนมัติ + รู้ทันทีว่าหมดอายุ (REALTIME_DESIGN.md §6)
// -----------------------------------------------------------------------------
// ปัญหาที่แก้: เดิมโทเคนผู้ปกครองอายุ 10 นาที และ **ไม่มีอะไรบอกหน้าจอว่ามันหมดแล้ว** ผู้ใช้
// จึงรู้ตัวก็ต่อเมื่อกดปุ่มแล้วเด้งออกกลางคัน — กรอกฟอร์มมาทั้งหน้าก็หายหมด
//
// กติกา (§6):
//  - เซิร์ฟเวอร์ส่ง `session-expiring` มาก่อนหมดอายุ ~1 นาที
//  - **มีการใช้งานจริงใน 2 นาทีล่าสุด → ต่ออายุเงียบ ๆ ผู้ใช้ไม่เห็นอะไรเลย**
//  - ไม่มีการใช้งาน → ขึ้นแถบนับถอยหลัง + ปุ่ม "ใช้งานต่อ" · ขยับเมื่อไหร่ต่อให้ทันที
//  - หมดจริง → **ซ้อนหน้าล็อกอินทับ ไม่เด้งออกจากหน้าที่เปิดอยู่** ข้อมูลในฟอร์มต้องไม่หาย
//
// หลักการที่ห้ามละเมิด:
//  1. **"ใช้งานอยู่" นับจากการกระทำของคนเท่านั้น** (`mousedown`/`keydown`/`touchstart`/`scroll`)
//     ห้ามนับโพลเบื้องหลังหรือสัญญาณที่วิ่งเข้ามา — ไม่งั้นแท็บที่ถูกลืมเปิดทิ้งไว้จะต่ออายุ
//     ตัวเองตลอดกาล แล้ว idle timeout ฝั่งเซิร์ฟเวอร์ไม่มีความหมายเลย (หลักการเดียวกับ
//     `IDLE_IGNORE` ฝั่ง backend)
//  2. **หลายแท็บต้องมีแท็บเดียวที่ต่ออายุ** — ไม่งั้นเปิด 5 แท็บ = ยิง `/renewSession` 5 ครั้ง
//     พร้อมกันทุกครึ่งชั่วโมง · ใช้ localStorage เป็นตัวตัดสิน แล้วแจกโทเคนใหม่ให้แท็บอื่น
//     ผ่าน event `storage` (แท็บอื่นได้โทเคนใหม่โดยไม่ต้องยิงเอง)
//  3. **ห้าม throw** — ต่ออายุไม่สำเร็จต้องกลายเป็น "ขึ้นหน้าล็อกอิน" ไม่ใช่ "แอปพัง"
// -----------------------------------------------------------------------------
import axios from 'axios';
import live from './live';

const ACTIVE_WINDOW_MS = 2 * 60 * 1000;   // "เพิ่งใช้งาน" = ภายใน 2 นาที (§6)
const CHECK_EVERY_MS = 30 * 1000;         // รอบตรวจอายุโทเคนด้วยตัวเอง
const RENEW_BEFORE_MS = 5 * 60 * 1000;    // เหลือน้อยกว่านี้ + มีการใช้งาน = ต่อเลย ไม่รอสัญญาณ
const RENEW_LOCK_KEY = 'session_renew_lock';
const RENEW_LOCK_MS = 10000;              // กันแท็บอื่นยิงซ้ำระหว่างที่แท็บนี้กำลังต่อ
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange'];

let lastActivity = Date.now();
let handlers = null;
let cb = {};                              // { onExpiring, onEnded, onRenewed }
let expiresAt = 0;                        // เวลาหมดอายุของโทเคนใบปัจจุบัน (ms)
let ticker = null;

/** อ่าน exp จากโทเคนเอง — ไม่ต้องเชื่อว่ามีใครบอกมา */
function expiryOf(token) {
  try { return JSON.parse(atob(String(token).split('.')[1])).exp * 1000; }
  catch (e) { return 0; }
}

function markActivity() { lastActivity = Date.now(); }

/** เพิ่งมีการกระทำของคนจริง ๆ ไหม (หลักการข้อ 1) */
function isActive() { return Date.now() - lastActivity < ACTIVE_WINDOW_MS; }

/** แท็บนี้ได้สิทธิ์ต่ออายุไหม — แท็บเดียวเท่านั้น (หลักการข้อ 2) */
function claimRenewLock() {
  try {
    const now = Date.now();
    const prev = Number(localStorage.getItem(RENEW_LOCK_KEY) || 0);
    if (now - prev < RENEW_LOCK_MS) return false;   // แท็บอื่นเพิ่งจับไป
    localStorage.setItem(RENEW_LOCK_KEY, String(now));
    return true;
  } catch (e) {
    return true;   // localStorage ใช้ไม่ได้ (โหมดส่วนตัวบางตัว) — ยอมให้ต่อ ดีกว่าไม่ต่อเลย
  }
}

/**
 * ต่ออายุ — คืน true เมื่อสำเร็จ
 * ใบใหม่ถูกเก็บลง localStorage + store ทันที ทุก request หลังจากนี้จะหยิบใบใหม่ไปเองเพราะ
 * ทุกจออ่าน `$store.getters.getToken` ตอนเรียก ไม่ได้จำไว้ตั้งแต่ตอน mount
 */
async function renew(store, { force = false } = {}) {
  const token = store.getters.getToken;
  if (!token) return false;
  if (!force && !claimRenewLock()) return true;   // แท็บอื่นกำลังต่อให้อยู่ — ถือว่าเรียบร้อย
  const releaseLock = () => { try { localStorage.removeItem(RENEW_LOCK_KEY); } catch (e) { /* ไม่เป็นไร */ } };

  try {
    const res = await axios.post(((env && env.SERVER_URL) || '') + '/renewSession', {},
      { headers: { Authorization: `Bearer ${token}` } });
    if (!res.data || !res.data.token) { releaseLock(); return false; }
    // เขียน localStorage ด้วย → แท็บอื่นได้ event `storage` แล้วรับใบใหม่ไปใช้โดยไม่ต้องยิงเอง
    localStorage.setItem('token', res.data.token);
    store.dispatch('setToken', { token: res.data.token, userdata: localStorage.getItem('userdata') });
    expiresAt = res.data.expiresAt || expiryOf(res.data.token);
    releaseLock();
    if (cb.onRenewed) cb.onRenewed(res.data);
    return true;
  } catch (e) {
    // 🔑 **แยก "ถูกปฏิเสธ" ออกจาก "ยิงไม่ถึง"** — เน็ตยิมสะดุด 2 วิ ไม่ใช่เหตุให้บังคับล็อกอินใหม่
    // ทั้งที่โทเคนยังเหลืออายุอยู่ · คืน 'retry' ให้ผู้เรียกลองใหม่ แทนที่จะเด้ง overlay ทันที
    releaseLock();
    const st = e.response && e.response.status;
    if (st === 401 || st === 403 || st === 409) return false;   // ถูกปฏิเสธจริง
    return 'retry';                                             // ยิงไม่ถึง/เซิร์ฟเวอร์สะดุด
  }
}

/**
 * เริ่มทำงาน — เรียกครั้งเดียวหลังล็อกอินสำเร็จ
 * @param {object} store  Vuex store
 * @param {object} callbacks { onExpiring(secondsLeft) · onEnded(reason) · onRenewed(data) }
 */
function start(store, callbacks = {}) {
  stop();
  cb = callbacks;
  lastActivity = Date.now();

  // §6 นับ `visibilityState` ด้วย — สลับกลับมาที่แท็บคือการกระทำของคน ไม่ใช่โพลเบื้องหลัง
  const onActivity = (e) => {
    if (e && e.type === 'visibilitychange' && document.visibilityState !== 'visible') return;
    markActivity();
  };
  for (const ev of ACTIVITY_EVENTS) document.addEventListener(ev, onActivity, { passive: true });

  // แท็บอื่นต่ออายุสำเร็จ → รับใบใหม่มาใช้เลย ไม่ต้องยิงเอง (หลักการข้อ 2)
  const onStorage = (e) => {
    if (e.key !== 'token') return;
    // แท็บอื่นกด logout → ลบ token · แท็บนี้ต้องรู้ตัว ไม่ใช่ถือโทเคนที่ถูกเพิกถอนไปแล้วต่อ
    if (!e.newValue) { if (cb.onEnded) cb.onEnded('logout'); return; }
    if (e.newValue === store.getters.getToken) return;
    store.dispatch('setToken', { token: e.newValue, userdata: localStorage.getItem('userdata') });
    expiresAt = expiryOf(e.newValue);
    if (cb.onRenewed) cb.onRenewed({ token: e.newValue, fromOtherTab: true });
  };
  window.addEventListener('storage', onStorage);

  // 🔴 **ตัวจับเวลาเป็นเส้นทางหลัก สัญญาณ WS เป็นแค่ตัวเร่ง** — ห้ามให้การต่ออายุพึ่งสาย
  // เพราะทุกทางที่ทำให้สายใช้ไม่ได้จะกลายเป็นสวิตช์เตะคนทั้งระบบทุก 30 นาที: สวิตช์ปิดฉุกเฉิน
  // (`/live-ticket` ตอบ 503) · ชนเพดานสาย · prod ที่ยังไม่มี endpoint · proxy องค์กรบล็อก wss
  // — สวิตช์ที่มีไว้กู้สถานการณ์ต้องไม่ทำให้สถานการณ์แย่ลง
  expiresAt = expiryOf(store.getters.getToken);
  ticker = setInterval(async () => {
    if (!store.getters.getToken || !expiresAt) return;
    const left = expiresAt - Date.now();
    if (left > RENEW_BEFORE_MS) return;
    if (isActive()) { await renew(store); return; }      // ยังใช้งานอยู่ → ต่อเงียบ ๆ
    if (left <= 60 * 1000 && cb.onExpiring) cb.onExpiring(Math.max(0, Math.round(left / 1000)));
  }, CHECK_EVERY_MS);

  // สัญญาณจากเซิร์ฟเวอร์ (มาถึงก่อนตัวจับเวลา = ตอบสนองไวขึ้น แต่ไม่มีก็ยังทำงาน)
  const offSystem = live.onSystem(async (msg) => {
    if (msg.topic === 'session-expiring') {
      // เพิ่งใช้งาน → ต่อเงียบ ๆ ผู้ใช้ไม่เห็นอะไรเลย · ไม่ได้ใช้ → ให้ App.vue ขึ้นแถบถาม
      if (isActive()) {
        if (await renew(store)) return;
      }
      if (cb.onExpiring) cb.onExpiring(Math.max(0, Math.round(((msg.expiresAt || 0) - Date.now()) / 1000)));
      return;
    }
    if (msg.topic === 'session-ended') {
      if (cb.onEnded) cb.onEnded(msg.reason || 'expired');
    }
  });

  handlers = () => {
    for (const ev of ACTIVITY_EVENTS) document.removeEventListener(ev, onActivity);
    window.removeEventListener('storage', onStorage);
    offSystem();
  };
}

function stop() {
  if (handlers) { handlers(); handlers = null; }
  if (ticker) { clearInterval(ticker); ticker = null; }
  cb = {};
}

/** เรียกหลังล็อกอินใหม่ — ตั้งนาฬิกาใหม่ตามโทเคนใบล่าสุด */
function noteNewToken(token) { expiresAt = expiryOf(token); lastActivity = Date.now(); }

export default { start, stop, renew, isActive, markActivity, noteNewToken };
